import ApiService from '../../../core/services/ApiService';
import { normalizeTaxYear } from '../../../shared/utils/NormalizeTaxYear';

export interface CalcProfileItem {
  id: string;
  standardCalculationId: string;
  standardCalculationName: string;
  category: string;
  type: string;
  templateId: string | null;
  templateName: string | null;
  templatePath: string | null;
  versionOverride: string | null;
  sortOrder: number;
  status: string;
}

export interface CalcProfile {
  id: string;
  partnershipId: string;
  taxYear: string;
  name: string;
  status: string;
  items: CalcProfileItem[];
}

export interface UpsertCalcProfileItemPayload {
  standardCalculationId: string;
  templateId: string | null;
  versionOverride: string | null;
  sortOrder?: number;
  templateName?: string | null;
  templatePath?: string | null;
}

export interface CalcProfileUpdateResultDto {
  profile: CalcProfile;
  autoAddedItemNames: string[];
  newItemSortOrder: number;
}

class CalcProfileService extends ApiService {
  getProfile(partnershipId: string, taxYear: string): Promise<CalcProfile> {
    return this.makeRequest<CalcProfile>(
      `/api/calc-profile?partnershipId=${partnershipId}&taxYear=${normalizeTaxYear(taxYear)}`,
      { extractData: true },
    );
  }

  addItem(
    partnershipId: string,
    taxYear: string,
    payload: UpsertCalcProfileItemPayload,
  ): Promise<CalcProfileUpdateResultDto> {
    return this.makeRequest<CalcProfileUpdateResultDto>(
      `/api/calc-profile/items?partnershipId=${partnershipId}&taxYear=${normalizeTaxYear(taxYear)}`,
      { method: 'POST', data: payload as unknown as Record<string, unknown>, extractData: true },
    );
  }

  updateItem(itemId: string, payload: UpsertCalcProfileItemPayload): Promise<CalcProfileItem> {
    return this.makeRequest<CalcProfileItem>(
      `/api/calc-profile/items/${itemId}`,
      { method: 'PUT', data: payload as unknown as Record<string, unknown>, extractData: true },
    );
  }

  deleteItem(itemId: string): Promise<void> {
    return this.makeRequest<void>(
      `/api/calc-profile/items/${itemId}`,
      { method: 'DELETE', extractData: true },
    );
  }

  assignTemplate(itemId: string, formData: FormData): Promise<{ templateFileName?: string }> {
    return this.makeRequest<{ templateFileName?: string }>(
      `/api/calc-profile/items/${itemId}/template`,
      { method: 'POST', data: formData as unknown as Record<string, unknown>, extractData: true },
    );
  }
}

export const calcProfileService = new CalcProfileService();
